import { ColumnListDto } from "@common/dto/column/list-dto";
import { HttpSuccessDto } from "@common/dto/http";
import { IndividualFamily } from "@common/dto/individual/family";
import { IndividualListDto } from "@common/dto/individual/list-dto";
import { InviteHistoryDto } from "@common/dto/invite-history/list-dto";
import { InviteListDto } from "@common/dto/invite/list-dto";
import { QuestionnaireListDto } from "@common/dto/questionnaire/list-dto";
import { QuestionnaireItemMapDto } from "@common/dto/questionnaire/questionnaire-item-map-dto";
import { TableColumnFilter } from "@common/dto/table/filter";
import { clientTransformed } from "@common/utils/rest";
import { executeUI } from "@common/utils/ui-bridge";

const URL_PREFIX = 'api/individuals';
const NEW_URL_PREFIX = 'api/invites';

const getUrl = (path: string) => {
  return `${URL_PREFIX}/${path}`;
};

const getNewUrl = (path: string) => {
    return `${NEW_URL_PREFIX}/${path}`;
};

export const renameIndividual = (globalId: string | number, individualName: string) => {
	return clientTransformed({
    path: getUrl('renameIndividual'),
    method: 'POST',
		params: {
      globalId,
      individualName,
    },
  });
};

const moduleName = 'INDIVIDUALS';

export const openDatasheetFromIndividual = (globalId: string | number) => {
  executeUI(moduleName, 'openDatasheet', globalId, '');
};

export const openPedigreeFromIndividual = (globalId: string | number) => {
  executeUI(moduleName, 'openPedigree', globalId, '');
};

export const openTestResultsFromIndividual = (globalId: string | number) => {
  executeUI(moduleName, 'openTestResults', globalId, '');
};

export const generateLetterFromIndividual = (globalId: string | number) => {
  executeUI(moduleName, 'generateLetter', globalId, '');
};

export const orderTestFromIndividual = (globalId: string | number) => {
  executeUI(moduleName, 'orderTest', globalId, '');
};

export const getFamilyIdMembersCount = (familyId: IndividualListDto['familyId'] | number): Promise<number> => {
	return clientTransformed({
    path: getUrl('getFamilyIdMemberCount'),
    method: 'GET',
		params: {
      familyId
    }
  }).then((response: HttpSuccessDto<number>) => {
    return response.entity;
  });
};

export const getSentQuestionnaires = async (globalId: InviteListDto['id']): Promise<Array<QuestionnaireItemMapDto>> => {
  return clientTransformed({
    path: getUrl('openQuestionnaire'),
    params: {
      globalId,
    },
    method: 'GET'
  }).then(
    (response: HttpSuccessDto<Array<QuestionnaireItemMapDto>>) => {
      return response.entity;
    },
  );
};

export type InviteUpdateLockStatusParams = {
  inviteId: InviteListDto['id'];
  lock: boolean;
  fhq4: InviteListDto['fhq4'];
}

export const updateLockStatus = async (data: InviteUpdateLockStatusParams) => {
  return clientTransformed({
    path: getUrl('lockInvite'),
    method: 'POST',
		entity: data,
  });
};

export const getInviteHistory = async (params: {
  inviteId?: InviteListDto["id"];
  globalId: InviteListDto["globalId"];
  fhq4?: InviteListDto["fhq4"];
}) => {
  return clientTransformed({
    path: getUrl("inviteHistory"),
    method: "GET",
    params,
  }).then((response: HttpSuccessDto<InviteHistoryDto[]>) => {
    return response.entity;
  });
};

export const getIndividualFamilyData = (globalId: IndividualListDto['id']): Promise<IndividualFamily> => {
	return clientTransformed({
    path: getUrl('getIndividualFamilyId'),
    method: 'GET',
		params: {
      globalId
    }
  }).then((response: HttpSuccessDto<IndividualFamily>) => {
    return response.entity;
  });
};

export const getIndividualEmail = async (params: { fhq4: boolean | undefined; individualId: IndividualListDto['id']; questionnaireId: QuestionnaireListDto['questionnaireId'] | undefined }): Promise<string> => {
    return clientTransformed({
    path: getNewUrl('checkEmail'),
    method: 'GET',
		params: {
            questionnaireId: params.questionnaireId,
            globalId: params.individualId,
            fhq4: params.fhq4,
        }
    }).then((response: HttpSuccessDto<string>) => {
        return response.entity;
    });
};

export type SaveIndividualEmailParams = {
  questionnaireId: QuestionnaireListDto['questionnaireId'];
  globalId: IndividualListDto['id'];
  email: string;
  fhq4: boolean;
  wdcFolderId?: QuestionnaireListDto['wdcFolderId'];
  inviteId: InviteListDto['id'];
};

export const saveIndividualEmail = (data: SaveIndividualEmailParams) => {
	return clientTransformed({
        path: getUrl('saveEmailForIndividual'),
        method: 'POST',
        entity: {
            questionnaireId: data.questionnaireId,
            globalId: data.globalId,
            email: data.email,
            fhq4: data.fhq4,
            wdcfolderId: data.wdcFolderId,
            inviteId: data.inviteId,
        }
    });
};

export type SendInviteParams = {
  questionnaireId: QuestionnaireListDto['questionnaireId'];
  globalId: IndividualListDto['id'];
  emailTemplate: QuestionnaireListDto['emailTemplate'];
  fhq4: boolean;
  reminder: boolean;
  inviteId?: InviteListDto['id'];
}

export const sendInvite = (data: SendInviteParams) => {
	return clientTransformed({
    path: getUrl('sendInvite'),
    method: 'POST',
		entity: data,
  });
};

export type GetIndividualsRequestFiltering = {
  filterApplied: boolean;
  columnFilters?: Array<TableColumnFilter>;
  columns: Array<ColumnListDto>;
  search?: string;
  probandsOnly?: boolean;
  folderId?: number;
};

export type GetIndividualsRequestPagination = {
  start: number;
  size: number;
};

export type GetIndividualsRequestParams = {
  filtering: GetIndividualsRequestFiltering;
  pagination: GetIndividualsRequestPagination;
};

export const getIndividuals = ({ filtering, pagination }: GetIndividualsRequestParams): Promise<{ data: Array<IndividualListDto>; totalCount: number; }> => {
	return clientTransformed({
    path: getUrl('smartlist'),
    method: 'POST',
		entity: {
			...filtering,
			...pagination,
		},
  }).then(
    (response: HttpSuccessDto<{ data: Array<IndividualListDto>; totalCount: number; }>) => {
      return response.entity;
    },
  );
};